"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, FileCheck, Globe, Layers, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
interface LanguageOption {
  code: string;
  label: string;
  flag: string;
  headline: string;
  subline: string;
  ctaText: string;
  secondaryText: string;
}
const languages: LanguageOption[] = [{
  code: "EN",
  label: "English (US / Global)",
  flag: "EN",
  headline: "Ready to specify your wire mesh?",
  subline: "Send your mesh count, aperture size, or target micron rating for a verified engineering quote within one business day.",
  ctaText: "Request a custom quote",
  secondaryText: "Technical specifications ready? Upload your drawing or data sheet."
}, {
  code: "ZH",
  label: "中文 (中国 / 全球)",
  flag: "ZH",
  headline: "准备好确定您的工业丝网规格了吗？",
  subline: "提供您的目数、孔径尺寸或目标微米级过滤精度，我们将在1个工作日内为您提供经工程验证的专业报价。",
  ctaText: "获取定制报价",
  secondaryText: "已备齐技术规格？欢迎在询价单中上传工程图纸或数据表。"
}, {
  code: "DE",
  label: "Deutsch (DACH)",
  flag: "DE",
  headline: "Edelstahldrahtgewebe direkt anfragen",
  subline: "Senden Sie Ihre Maschenweite, Drahtstärke oder gewünschte Filterfeinheit für ein detailliertes technisches Angebot.",
  ctaText: "Technisches Angebot anfordern",
  secondaryText: "Zeichnungen oder Spezifikationen direkt im Anfrageformular hochladen."
}, {
  code: "FR",
  label: "Français (Europe)",
  flag: "FR",
  headline: "Prêt à définir votre toile métallique?",
  subline: "Transmettez vos dimensions de maille, diamètre de fil ou seuil de filtration pour un devis technique sous 24h.",
  ctaText: "Demander un devis personnalisé",
  secondaryText: "Spécifications prêtes ? Joignez votre plan technique en ligne."
}, {
  code: "ES",
  label: "Español (Internacional)",
  flag: "ES",
  headline: "¿Listo para cotizar su malla de acero?",
  subline: "Envíe su número de malla, apertura o micraje objetivo para recibir una cotización técnica en 24 horas hábiles.",
  ctaText: "Solicitar cotización técnica",
  secondaryText: "¿Tiene planos técnicos? Adjunte sus requerimientos en el formulario."
}];
const technicalGuarantees = [{
  icon: Clock,
  titleEn: "24-Hour Turnaround",
  titleZh: "24小时快速响应",
  descEn: "Detailed pricing & lead times",
  descZh: "详尽价格与交货周期核算"
}, {
  icon: ShieldCheck,
  titleEn: "Material Certification",
  titleZh: "材质质量认证",
  descEn: "EN 10204 3.1 & PMI verified",
  descZh: "EN 10204 3.1 证书及 PMI 材质复验"
}, {
  icon: Layers,
  titleEn: "Custom Fabrication",
  titleZh: "深度定制加工",
  descEn: "Discs, coils, cylinders & packs",
  descZh: "滤圆片、分条卷网、滤筒及组合网"
}, {
  icon: FileCheck,
  titleEn: "Engineering Review",
  titleZh: "专业工程审核",
  descEn: "Direct assistance from specialists",
  descZh: "资深过滤工程师一对一技术协助"
}];
export default function QuoteCta() {
  const [lang, setLang] = useState<"en" | "zh">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("mf_lang");
      if (saved === "zh" || saved === "en") return saved;
    }
    return "en";
  });
  const [selectedLang, setSelectedLang] = useState<LanguageOption>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("mf_lang");
      if (saved === "zh") return languages[1];
    }
    return languages[0];
  });
  useEffect(() => {
    try {
      const savedLang = localStorage.getItem("mf_lang");
      if (savedLang === "zh" || savedLang === "en") {
        setLang(savedLang);
        setSelectedLang(savedLang === "zh" ? languages[1] : languages[0]);
      }
    } catch {
      // Ignore localStorage read errors in restricted environments
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "en" | "zh";
      }>;
      const nextLang = customEvent.detail?.lang || localStorage.getItem("mf_lang") as "en" | "zh";
      if (nextLang === "zh" || nextLang === "en") {
        setLang(nextLang);
        setSelectedLang(nextLang === "zh" ? languages[1] : languages[0]);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "mf_lang" && (e.newValue === "zh" || e.newValue === "en")) {
        setLang(e.newValue);
        setSelectedLang(e.newValue === "zh" ? languages[1] : languages[0]);
      }
    };
    window.addEventListener("mf-language-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("mf-language-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const isZh = lang === "zh";
  return;
}